// Display formatters shared by the client and partner apps.
//
// Moved verbatim from `apps/*/src/mocks/format.ts` (MOCK-01), where two
// identical copies sat inside the mock engine and were imported by 16 live
// screens. Nothing here is mock data — these are presentation helpers, which is
// why they land in `ui` rather than `logic`: `@bednbody/logic` is Deno-pure
// money math that the Edge Functions recompute the truth with, and a locale
// string has no business in that package.
//
// NOT moved, deliberately: `bookingRef` and `giftPassCode`. Both are mock
// GENERATORS with no consumer outside the mock engine — the server issues real
// booking refs (migration 0043, Manila-dated) and real GiftPass codes. They die
// with the engine in MOCK-05 rather than being given a home here.

export function formatPHP(n: number): string {
  const isWhole = Number.isInteger(n);
  const value = n.toLocaleString("en-PH", {
    minimumFractionDigits: isWhole ? 0 : 2,
    maximumFractionDigits: 2,
  });
  return `₱${value}`;
}

// "2027-05-01" -> "May 1, 2027" (en-PH). Parsed as local midnight so the date
// never shifts a day from UTC — matches the `T00:00:00` trick used across the
// booking/home screens.
export function formatDate(ymd: string): string {
  return new Date(`${ymd}T00:00:00`).toLocaleDateString("en-PH", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

// "18:30" -> "6:30 PM"
export function formatTime12h(time: string): string {
  const [hour = "0", minute = "00"] = time.split(":");
  const numericHour = Number(hour);
  const period = numericHour >= 12 ? "PM" : "AM";
  const displayHour = numericHour % 12 || 12;
  return `${displayHour}:${minute} ${period}`;
}

// "14:00" -> "2–3 PM" (booking slots are shown as the hour they cover, not a
// start time — Business Logic §14). Collapses the period when both ends share
// it: "2–3 PM", but "11 PM–12 AM".
export function formatSlotRange(start: string): string {
  const [hour = "0", minute = "00"] = start.split(":");
  const endHour = (Number(hour) + 1) % 24;
  const from = formatTime12h(start);
  const to = formatTime12h(`${endHour}:${minute}`);
  const [fromTime = "", fromPeriod = ""] = from.split(" ");
  const [toTime = "", toPeriod = ""] = to.split(" ");
  const trim = (t: string) => t.replace(":00", "");
  return fromPeriod === toPeriod
    ? `${trim(fromTime)}–${trim(toTime)} ${toPeriod}`
    : `${trim(fromTime)} ${fromPeriod}–${trim(toTime)} ${toPeriod}`;
}

// ISO -> "6:32 PM" (message bubble timestamps)
export function formatClockTime(iso: string): string {
  const d = new Date(iso);
  return formatTime12h(`${d.getHours()}:${String(d.getMinutes()).padStart(2, "0")}`);
}

// ISO -> "just now" / "5m ago" / "3h ago" / "2d ago"
export function formatTimeAgo(iso: string): string {
  const diffMs = Date.now() - new Date(iso).getTime();
  const min = Math.floor(diffMs / 60_000);
  if (min < 1) return "just now";
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  return `${Math.floor(hr / 24)}d ago`;
}

// { date, time, durationMin } -> "Today · 6:30 PM · 60 min"
export function formatScheduleLabel(schedule: {
  date: string;
  time: string;
  durationMin: number;
}): string {
  const today = new Date().toISOString().slice(0, 10);
  const dayLabel = schedule.date === today ? "Today" : schedule.date;
  return `${dayLabel} · ${formatTime12h(schedule.time)} · ${schedule.durationMin} min`;
}
